// Data Quality Rules API Types
// Aligned with backend API schema and includes client-side validation helpers

export type BackendRuleType =
    | 'null_check'
    | 'range_check'
    | 'length_check'
    | 'lov_check'
    | 'err_lov_check'
    | 'invalid_char_check'
    | 'pk_check'
    | 'data_type_check'
    | 'ai_generated_rule'
    | 'custom_rule';

export type FrontendRuleType =
    | 'NULL_CHECK'
    | 'RANGE_CHECK'
    | 'LENGTH_CHECK'
    | 'LOV_CHECK'
    | 'ERR_LOV_CHECK'
    | 'INVALID_CHAR_CHECK'
    | 'PK_CHECK'
    | 'DATA_TYPE_CHECK'
    | 'CUSTOM_RULE';

export interface BackendDQRule {
    id: string;
    rule_type: BackendRuleType;
    parameters: Record<string, any>;
    rule_description: string;
    data_object_id: string;
    column_name: string;
    created_by: string;
    created_at: string;
    updated_at: string;
    is_active: boolean; // Soft delete flag: true = exists, false = soft deleted
    is_enabled?: boolean; // Enable/disable flag: true = enabled, false = disabled
    visibility?: 'private' | 'public'; // Added for enhanced visibility control
}

// Frontend rule interface for UI components
export interface FrontendRule {
    rule_type: FrontendRuleType;
    rule_logic: string; // JSON string of parameters
    business_meaning: string;
    active_flag: boolean;
    created_by?: string;
    created_at?: string;
    column_name?: string; // Optional for some contexts
}

export interface CreateRuleRequest {
    rule_type: BackendRuleType;
    parameters: Record<string, any>;
    rule_description: string;
    data_object_id: string;
    column_name: string;
    visibility?: 'private' | 'public'; // Added for enhanced visibility control
    creation_method?: 'manual' | 'ai_generated'; // Added for creation tracking
    user_email?: string; // Added programmatically by service layer
}

// Interface for Databricks table rules (includes both data_object_id and table_name)
export interface CreateDatabricksRuleRequest {
    rule_type: BackendRuleType;
    parameters: Record<string, any>;
    rule_description: string;
    data_object_id: string;
    table_name: string;
    column_name: string;
    visibility?: 'private' | 'public'; // Added for enhanced visibility control
    creation_method?: 'manual' | 'ai_generated'; // Added for creation tracking
    user_email?: string; // Added programmatically by service layer
}

export interface BatchCreateRulesRequest {
    rules: CreateRuleRequest[];
}

// Batch request for Databricks table rules
export interface BatchCreateDatabricksRulesRequest {
    rules: CreateDatabricksRuleRequest[];
}

// Modern interface for create-or-reuse API structure
export interface ModernBatchCreateRulesRequest {
    rules: Array<CreateRuleRequest | ReuseRuleRequest | CreateDatabricksRuleRequest>;
}

// Interface for reusing existing rules
export interface ReuseRuleRequest {
    rule_id: string;
    data_object_id: string;
    column_name: string;
    table_name?: string; // For Databricks compatibility
}

export interface BatchCreateRulesResponse {
    created_rules: BackendDQRule[];
    failed_rules: Array<{
        index: number;
        rule_data: CreateRuleRequest;
        error: string;
    }>;
    total_requested: number;
    total_created: number;
    total_failed: number;
}

// Modern response interface matching documented API
export interface ModernBatchCreateRulesResponse {
    created_count: number;
    reused_count: number;
    failed_count: number;
    total_processed: number;
    created_rules: BackendDQRule[];
    reused_rules: Array<{
        rule_id: string;
        data_object_id: string;
        column_name: string;
        mapping_created: boolean;
    }>;
    failed_rules: Array<{
        rule_data?: CreateRuleRequest | ReuseRuleRequest;
        error: string;
    }>;
}

export interface UpdateRuleRequest {
    rule_description?: string;
    is_enabled?: boolean;
}

export interface GetRulesResponse {
    rules: BackendDQRule[];
    total_count: number;
    has_more: boolean;
}

export interface BatchDeleteRulesRequest {
    rule_ids: string[];
}

export interface BatchDeleteRulesResponse {
    deleted_rules: string[];
    failed_deletions: Array<{
        rule_id: string;
        error: string;
    }>;
    total_requested: number;
    total_deleted: number;
    total_failed: number;
}

// Type mapping helpers
export const FRONTEND_TO_BACKEND_RULE_TYPES: Record<FrontendRuleType, BackendRuleType> = {
    'NULL_CHECK': 'null_check',
    'RANGE_CHECK': 'range_check',
    'LENGTH_CHECK': 'length_check',
    'LOV_CHECK': 'lov_check',
    'ERR_LOV_CHECK': 'err_lov_check',
    'INVALID_CHAR_CHECK': 'invalid_char_check',
    'PK_CHECK': 'pk_check',
    'DATA_TYPE_CHECK': 'data_type_check',
    'CUSTOM_RULE': 'ai_generated_rule'
};

export const BACKEND_TO_FRONTEND_RULE_TYPES: Record<BackendRuleType, FrontendRuleType> = {
    'null_check': 'NULL_CHECK',
    'range_check': 'RANGE_CHECK',
    'length_check': 'LENGTH_CHECK',
    'lov_check': 'LOV_CHECK',
    'err_lov_check': 'ERR_LOV_CHECK',
    'invalid_char_check': 'INVALID_CHAR_CHECK',
    'pk_check': 'PK_CHECK',
    'data_type_check': 'DATA_TYPE_CHECK',
    ai_generated_rule: 'CUSTOM_RULE',
    custom_rule: 'CUSTOM_RULE'
};

// Validation constants and helpers
export const MAX_BATCH_SIZE = 50;
export const MAX_PARAMETER_SIZE_BYTES = 10 * 1024; // 10KB
export const MAX_DESCRIPTION_LENGTH = 500;

export interface ValidationResult {
    valid: boolean;
    errors: string[];
}

export function validateRuleParameters(ruleType: BackendRuleType, parameters: any): ValidationResult {
    const errors: string[] = [];

    switch (ruleType) {
        case 'null_check':
        case 'pk_check':
            // No parameters required
            break;

        case 'range_check':
            if (!parameters.min_value && !parameters.max_value) {
                errors.push('range_check requires at least one of min_value or max_value');
            }
            if (parameters.min_value !== undefined && typeof parameters.min_value !== 'number') {
                errors.push('min_value must be a number');
            }
            if (parameters.max_value !== undefined && typeof parameters.max_value !== 'number') {
                errors.push('max_value must be a number');
            }
            break;

        case 'length_check':
            if (!parameters.min_length && !parameters.max_length) {
                errors.push('length_check requires at least one of min_length or max_length');
            }
            if (parameters.min_length !== undefined && (typeof parameters.min_length !== 'number' || parameters.min_length < 0)) {
                errors.push('min_length must be a non-negative number');
            }
            if (parameters.max_length !== undefined && (typeof parameters.max_length !== 'number' || parameters.max_length < 0)) {
                errors.push('max_length must be a non-negative number');
            }
            break;

        case 'lov_check':
            if (!Array.isArray(parameters.values) || parameters.values.length === 0) {
                errors.push('lov_check requires a non-empty values array');
            }
            break;

        case 'err_lov_check':
            if (!Array.isArray(parameters.error_values) || parameters.error_values.length === 0) {
                errors.push('err_lov_check requires a non-empty error_values array');
            }
            break;

        case 'invalid_char_check':
            if (!Array.isArray(parameters.invalid_chars) || parameters.invalid_chars.length === 0) {
                errors.push('invalid_char_check requires a non-empty invalid_chars array');
            }
            break;

        case 'data_type_check':
            if (!parameters.expected_type || typeof parameters.expected_type !== 'string') {
                errors.push('data_type_check requires expected_type string');
            }
            const validTypes = ['integer', 'float', 'string', 'boolean', 'date'];
            if (parameters.expected_type && !validTypes.includes(parameters.expected_type)) {
                errors.push(`expected_type must be one of: ${validTypes.join(', ')}`);
            }
            break;

        case 'custom_rule':
            if (!parameters.operator || typeof parameters.operator !== 'string') {
                errors.push('custom_rule requires operator string');
            }
            if (parameters.value === undefined) {
                errors.push('custom_rule requires value');
            }
            break;

        case 'ai_generated_rule':
            if (!parameters.custom_sql || typeof parameters.custom_sql !== 'string') {
                errors.push('ai_generated_rule requires custom_sql string');
            }
            if (!parameters.validation_description || typeof parameters.validation_description !== 'string') {
                errors.push('ai_generated_rule requires validation_description string');
            }
            break;

        default:
            errors.push(`Unknown rule type: ${ruleType}`);
    }

    return {
        valid: errors.length === 0,
        errors
    };
}

export function validateBatchCreateRequest(request: BatchCreateRulesRequest | BatchCreateDatabricksRulesRequest): ValidationResult {
    const errors: string[] = [];

    if (!request.rules || !Array.isArray(request.rules)) {
        errors.push('rules must be an array');
        return { valid: false, errors };
    }

    if (request.rules.length === 0) {
        errors.push('rules array cannot be empty');
        return { valid: false, errors };
    }

    if (request.rules.length > MAX_BATCH_SIZE) {
        errors.push(`Batch size cannot exceed ${MAX_BATCH_SIZE} rules`);
    }

    // Validate each rule
    request.rules.forEach((rule, index) => {
        if (!rule.rule_type) {
            errors.push(`Rule ${index}: rule_type is required`);
        }

        // data_object_id is required for all rule types
        if (!('data_object_id' in rule) || !rule.data_object_id?.trim()) {
            errors.push(`Rule ${index}: data_object_id is required`);
        }

        // For Databricks rules, table_name is also required
        if ('table_name' in rule) {
            const tableName = (rule as any).table_name;
            if (!tableName || typeof tableName !== 'string' || !tableName.trim()) {
                errors.push(`Rule ${index}: table_name cannot be empty when provided`);
            }
        }

        if (!rule.column_name?.trim()) {
            errors.push(`Rule ${index}: column_name is required`);
        }

        if (rule.rule_description && rule.rule_description.length > MAX_DESCRIPTION_LENGTH) {
            errors.push(`Rule ${index}: description too long (max ${MAX_DESCRIPTION_LENGTH} chars)`);
        }

        // Validate parameters size
        const paramStr = JSON.stringify(rule.parameters || {});
        if (new Blob([paramStr]).size > MAX_PARAMETER_SIZE_BYTES) {
            errors.push(`Rule ${index}: parameters exceed size limit (${MAX_PARAMETER_SIZE_BYTES} bytes)`);
        }

        // Validate rule-specific parameters
        if (rule.rule_type) {
            const paramValidation = validateRuleParameters(rule.rule_type, rule.parameters);
            if (!paramValidation.valid) {
                paramValidation.errors.forEach(error => {
                    errors.push(`Rule ${index}: ${error}`);
                });
            }
        }
    });

    return {
        valid: errors.length === 0,
        errors
    };
}

// Sanitization helpers
export function sanitizeDescription(description: string): string {
    if (!description) return '';

    // Strip HTML tags and potentially dangerous content
    return description
        .replace(/<[^>]*>/g, '') // Remove HTML tags
        .replace(/javascript:/gi, '') // Remove javascript: protocol
        .replace(/on\w+\s*=/gi, '') // Remove event handlers
        .trim()
        .slice(0, MAX_DESCRIPTION_LENGTH);
}